// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import {
  useEvolvePaginatedGet,
  useEvolvePatch,
  type FacilityId,
  type Project,
  type WorkCellTask,
  type RenderableUser,
  type WorkOrder,
  type WorkOrderStateName,
} from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import type { SearchableMultiSelectProps } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableMultiSelect';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WithNullable } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  user: RenderableUser;
  opened: boolean;
  onClose: () => void;
  onSubmit: (workCellTask: WorkCellTask) => void;
  facilityId: FacilityId;
};

export const SelectableWorkCellTaskItem: SearchableMultiSelectProps<
  'shop/workCellTask',
  WorkCellTask,
  'workCellTaskId'
>['renderOption'] = ({ option: { original: workCellTask } }) => (
  <Stack gap={0}>
    <Text>{workCellTask.taskName}</Text>
    <Flex gap={4} align="center" fz="xs">
      <Text c="dimmed">{workCellTask.projectName}</Text>
      <EvolveIcon icon="ArrowRight" color="gray.5" size={8} />
      <Text c="dimmed">{getDisplayNameWO(workCellTask)}</Text>
      <EvolveIcon icon="ArrowRight" color="gray.5" size={8} />
      <Text c="dimmed">{workCellTask.workOrderItemName ?? workCellTask.workOrderItemName}</Text>
    </Flex>
  </Stack>
);

type ValidatedFormBody = {
  project: Project | null;
  workOrder: WorkOrder | null;
  workCellTask: WorkCellTask;
};

type FormBody = WithNullable<ValidatedFormBody, 'workCellTask'>;

const AssignUserToTaskFields = ({ user, onSubmit, facilityId }: Pick<Props, 'user' | 'onSubmit' | 'facilityId'>) => {
  const { states } = useWorkOrderStatuses();
  const projectsPaginatedGet = useEvolvePaginatedGet('shop/project');

  const form = useForm<FormBody>({
    initialValues: {
      project: null,
      workOrder: null,
      workCellTask: null,
    },
    validate: {
      workCellTask: (v) => isNil(v),
    },
  });
  const { project, workOrder } = form.values;

  const workOrderStateIds = useMemo(
    () =>
      states
        ?.filter((s) => (['Not Started', 'In Progress'] as WorkOrderStateName[]).includes(s.name))
        .map((s) => s.stateId),
    [states],
  );

  const {
    setDefaultOpts: setDefaultOptsWorkOrders,
    reset: resetWorkOrders,
    ...workOrdersPaginatedGet
  } = useEvolvePaginatedGet('shop/workOrder', {
    defaultConfig: {
      params: {
        facilityId,
        workOrderStateIds,
      },
    },
  });

  useEffect(() => {
    setDefaultOptsWorkOrders({
      defaultConfig: {
        params: {
          facilityId,
          projectId: project?.projectId,
          workOrderStateIds,
        },
      },
    });
  }, [facilityId, project?.projectId, setDefaultOptsWorkOrders, workOrderStateIds]);

  const {
    setDefaultOpts: setDefaultOptsWorkCellTasks,
    reset: resetWorkCellTasks,
    ...workCellTasksPaginatedGet
  } = useEvolvePaginatedGet('shop/workCellTask', {
    defaultConfig: { params: { facilityIds: facilityId } },
  });

  useEffect(() => {
    setDefaultOptsWorkCellTasks({
      defaultConfig: {
        params: {
          facilityIds: facilityId,
          projectIds: project?.projectId,
          workOrderName: workOrder?.name,
        },
      },
    });
    resetWorkCellTasks();
  }, [facilityId, resetWorkCellTasks, project?.projectId, workOrder?.name, setDefaultOptsWorkCellTasks]);

  const { apiCall: patchTask } = useEvolvePatch(`shop/task/:taskId`);

  const assignUserToTask = async (rawValues: typeof form.values) => {
    const { workCellTask } = rawValues as ValidatedFormBody;
    await patchTask(
      { assignees: [...workCellTask.assignees.map((a) => a.user.userId), user.userId] },
      {
        url: `shop/task/${workCellTask.taskId}`,
      },
    ).then((updatedTask) => {
      onSubmit(workCellTask);
      notifications.show({
        title: 'Successfully assigned',
        message: `${validateUserNameOrEmail(user)} assigned to task ${updatedTask.taskName}`,
        color: 'green',
      });
    });
  };

  useFormPopover(form, assignUserToTask);

  const workOrderIsBlank = isNil(workOrder);

  return (
    <Stack gap="xs">
      <SearchableSelect
        label="Project Filter"
        idKey="projectId"
        paginatedGet={projectsPaginatedGet}
        getItemLabel={(p) => p.projectName}
        preloadData
        clearable
        value={project}
        onChange={(p) => form.setFieldValue('project', p)}
      />
      <SearchableSelect
        label="Work Order"
        idKey="workOrderId"
        paginatedGet={{ reset: resetWorkOrders, ...workOrdersPaginatedGet }}
        getItemLabel={(wo) => getDisplayNameWO(wo)}
        preloadData
        clearable
        required
        value={workOrder}
        onChange={(wo) => form.setValues({ workOrder: wo, workCellTask: null })}
      />
      <SearchableSelect
        label="Task"
        idKey="taskId"
        paginatedGet={{ reset: resetWorkCellTasks, ...workCellTasksPaginatedGet }}
        getItemLabel={(t) => t.taskName}
        renderOption={SelectableWorkCellTaskItem}
        required
        preloadData
        placeholder={workOrderIsBlank ? 'Select a work order first' : 'Select a task'}
        w="100%"
        disabled={workOrderIsBlank}
        {...form.getInputProps('workCellTask')}
      />
    </Stack>
  );
};

export const AssignUserToTaskPopover = ({ user, opened, onClose, onSubmit, facilityId }: Props) => (
  <FormPopover opened={opened} onClose={onClose} position="left-start">
    <AssignUserToTaskFields user={user} onSubmit={onSubmit} facilityId={facilityId} />
  </FormPopover>
);
